import React from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";

export type TableActionButtonVariant =
  | "outline-primary"
  | "outline-secondary"
  | "outline-success"
  | "outline-warning"
  | "outline-danger";

export interface TableActionButtonProps {
  /**
   * The icon to display inside the button. Accepts either:
   * - A React node (e.g. `<FiEdit2 />` from react-icons/fi) — preferred for new usage
   * - A string Bootstrap-icon name without the `bi-` prefix (e.g. `"pencil"`) — legacy
   */
  icon: React.ReactNode;
  /** Tooltip text shown on hover — tells every user exactly what the button does. */
  label: string;
  onClick: () => void;
  variant?: TableActionButtonVariant;
  disabled?: boolean;
  "data-testid"?: string;
  size?: "sm";
  /** Extra class(es) appended to the button — e.g. a hover-colour modifier. */
  className?: string;
  /**
   * When true, render the `label` as visible text beside the icon (icon + text)
   * and drop the hover tooltip. When false (default) the button is icon-only
   * with a tooltip — preserving the original compact behaviour.
   */
  showText?: boolean;
  /**
   * Hint for the `TableActions` wrapper: mark this as one of the (up to 2)
   * actions shown inline on desktop. Un-flagged actions overflow into the ⋮
   * menu. Ignored when the button is rendered on its own.
   */
  primary?: boolean;
  /** Shown as the tooltip (and in the ⋮ menu) while `disabled`, e.g. why the action is blocked. */
  disabledReason?: string;
}

/**
 * Icon-only action button for table rows.
 *
 * - Renders only an icon (no text) so tables stay compact.
 * - Wraps in a Bootstrap Tooltip so novice users see the action label on hover.
 * - Uses `aria-label` so screen readers announce the action correctly.
 */
const TableActionButton: React.FC<TableActionButtonProps> = ({
  icon,
  label,
  onClick,
  variant = "outline-primary",
  disabled = false,
  "data-testid": dataTestId,
  size = "sm",
  className,
  showText = false,
  disabledReason,
}) => {
  const iconNode =
    typeof icon === "string" ? (
      <i className={`bi bi-${icon}`} aria-hidden />
    ) : (
      icon
    );

  // Text mode resolves the default variant to a neutral outline so row actions
  // never look like a primary call-to-action.
  const resolvedVariant = showText && variant === "outline-primary" ? "outline-secondary" : variant;

  const button = (
    <Button
      variant={resolvedVariant}
      size={size}
      onClick={onClick}
      disabled={disabled}
      className={`ntcog-table-action-btn${showText ? " ntcog-table-action-btn--text" : ""}${className ? ` ${className}` : ""}`}
      aria-label={label}
      data-testid={dataTestId}
    >
      {iconNode}
      {showText && <span className="ms-1">{label}</span>}
    </Button>
  );

  const blockedReason = disabled && disabledReason ? disabledReason : undefined;

  // With visible text the tooltip is redundant; show it only in icon-only mode or to explain a block.
  if (showText && !blockedReason) {
    return button;
  }

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={`tooltip-action-${label.replace(/\s+/g, "-").toLowerCase()}`}>{blockedReason ?? label}</Tooltip>}
      children={<span className="d-inline-block">{button}</span>}
    />
  );
};

export default TableActionButton;
